import { isClassified, isSupportRole } from '../utils/runner-role.js'
import { ref, toRaw } from 'vue'
import { defineStore } from 'pinia'
import { useStateStore } from './state.js'

// Wydajność (26.10.2026): wszystko, co przegląda całą listę, liczy się raz na pobranie /splits,
// na zwykłych obiektach (przed przypisaniem do stanu) — przez reaktywne obiekty Vue każdy odczyt
// pola jest kilka razy droższy. Wcześniej przypisanie podbić (zawodnicy × podbicia), miejsca
// w kategorii i K/M (dla każdego zawodnika osobne sortowanie grupy) i pytania szablonów w każdym
// wierszu („czy są punkty / płeć”) dawały przy 1000 zawodnikach ~6 s na komputerze i ~20 s na telefonie.

// Podbicia pogrupowane po zawodniku, w kolejności z API (klucz jako tekst — API bywa niekonsekwentne
// z typami, a dawny kod porównywał `split.runner_id == runner.id`).
function groupSplitsByRunner(splitList) {
  const byRunner = new Map()
  for (const split of splitList) {
    const key = String(split.runner_id)
    let list = byRunner.get(key)
    if (!list) {
      list = []
      byRunner.set(key, list)
    }
    list.push(split)
  }
  return byRunner
}

// Odpowiedzi na „czy na liście są punkty / płeć / kategorie” zapamiętane dla danej tablicy wyników
// (fetchResults i applyFinishPunch zawsze podstawiają nową tablicę).
function memoForList(cache, list, compute) {
  const raw = toRaw(list)
  if (!cache.has(raw)) cache.set(raw, compute(raw))
  return cache.get(raw)
}

// Data startu wydarzenia z /events/alias jako czas ścienny — tak samo jak pozostałe pola
// (new Date bez strefy), żeby różnica meta − start była spójna. „Z” z API to też czas polski.
function eventStartAsWallClock(dateStart) {
  if (!dateStart) return null
  const text = String(dateStart).trim().replace(' ', 'T').replace(/Z$/, '').replace(/\.\d+$/, '')
  const date = new Date(text)
  return Number.isNaN(date.getTime()) ? null : date
}

export const useResultsStore = defineStore('results', () => {
   const apiUrl = process.env.VUE_APP_API_URL
  const results = ref([])
  const markers = ref([])
  const markersMap = ref({})
  const splits = ref([])
  const splitsMap = ref([])
  const event = ref(null)
  const categories = ref([])
  const leader = ref(null)
  // start wydarzenia dla zawodników bez start_time (ustawiany w fetchResults, tylko gdy
  // /events/alias dotyczy tego samego wydarzenia — magazyn bywa jeszcze po poprzednim)
  let fallbackStart = null

  function fetchResults(eventAlias) {
    return fetch(`${apiUrl}/api/v1/splits/${eventAlias}`)
      .then(response => response.json())
      .then(data => {
        const loadedEvent = event.value?.event
        fallbackStart = loadedEvent && loadedEvent.alias === eventAlias ? eventStartAsWallClock(loadedEvent.date_start) : null
        // Store categories data from splits response
        categories.value = data.categories || []

        markers.value = data.markers.sort((a, b) => {
          return a.sort - b.sort
        }) || []
        splits.value = data.splits || []
        markersMap.value = getMarkersMap()
        markersMap.value = Object.fromEntries(
          Object.entries(markersMap.value)
            .sort(([, a], [, b]) => a.sort - b.sort)
        )
        splitsMap.value = getSplitsMap()
        // zwykłe obiekty z odpowiedzi (data.markers jest już posortowane w miejscu powyżej)
        const plainMarkers = data.markers
        const plainSplits = data.splits || []
        const orderedMarkers = [...plainMarkers].sort((a, b) => a.sort - b.sort)
        const splitsByRunner = groupSplitsByRunner(plainSplits)
        const finishMarker = plainMarkers.find(marker => marker.type == 8 || marker.type == 93)

        data.runners.forEach(runner => {
          const runnerSplits = splitsByRunner.get(String(runner.id)) || []
          const startTime = getStartTime(runner)
          const penaltySeconds = getPenaltySeconds(runner)
          const penaltyMilliseconds = getPenaltyMilliseconds(penaltySeconds)

          runner.penaltySeconds = penaltySeconds
          runner.penaltyComment = runner.penalty_comment || ''
          
          // Calculate elapsed time for time-based calculations
          if (runner.finish_time) {
            const finishTime = new Date(runner.finish_time)
            if (startTime) {
              let elapsedTime = finishTime - startTime
              // If elapsed time is negative, set to 0
              if (elapsedTime < 0) {
                elapsedTime = 0
              }
              elapsedTime += penaltyMilliseconds
              runner.elapsedTime = elapsedTime
              // Hide zero times in display
              runner.timeScore = elapsedTime === 0 ? '' : getHHMMSSString(new Date(elapsedTime))
            }
          } else {
            let gpsFinishTime = getGpsFinishTime(runner, finishMarker, runnerSplits)
            if (gpsFinishTime) {
              let elapsedTime = gpsFinishTime - startTime
              // If elapsed time is negative, set to 0
              if (elapsedTime < 0) {
                elapsedTime = 0
              }
              elapsedTime += penaltyMilliseconds
              runner.elapsedTime = elapsedTime
              // Hide zero times in display
              runner.timeScore = elapsedTime === 0 ? '' : getHHMMSSString(new Date(elapsedTime))
            } else {
              runner.timeScore = '--:--:--'
            }
          }
          
          // Handle point-based scores (if runner.score is already a number from API)
          // Keep the original score if it's numeric, otherwise use time-based score
          if (typeof runner.score === 'number' && runner.score >= 0) {
            runner.pointScore = runner.score
          } else {
            runner.pointScore = null
            // Set score to time-based score for backward compatibility
            runner.score = runner.timeScore
          }
          runner.splits = {}
          runnerSplits.forEach(split => {
            runner.splits[split.marker_id] = split
          })
          // calculate last marker by sort and find the last split
          plainMarkers.forEach(marker => {
            let split = runner.splits[marker.id]
            if (split) {
              if (!runner.lastMarker || marker.sort > runner.lastMarker.sort) {
                runner.lastMarker = marker
                runner.lastSplit = split
              }
            }
          })

          const unfinishedProgress = getUnfinishedProgress(runner, orderedMarkers)
          runner.unfinishedSplitCount = unfinishedProgress.count
          runner.unfinishedLastSplitTime = unfinishedProgress.lastSplitTime
          runner.punchedCount = Object.keys(runner.splits || {}).length

        })
        
        const sorted = sortRunners(data.runners)

        // Leader (fastest finisher who is not DNS/DNF) and category / K-M positions,
        // computed on plain objects before they become reactive state
        calculateLeader(sorted)
        calculateCategoryPositions(sorted)

        results.value = sorted
      })
      .catch(error => {
        console.error('Error fetching results:', error)
      })
  }

  function sortRunners(runners) {
    // Check if any runner has point-based scores to determine sorting method
    const hasPointScores = runners.some(runner => runner.pointScore !== null && runner.pointScore > 0)

    return runners.slice().sort((a, b) => {
      if (a.abs) {
        return 1
      }
      if (b.abs) {
        return -1
      }
      if (a.dnf) {
        return 1
      }
      if (b.dnf) {
        return -1
      }

      // If we have point-based scores, sort by points first (higher points = better position)
      if (hasPointScores) {
        const aPoints = a.pointScore || 0
        const bPoints = b.pointScore || 0

        // If both have points or both don't have points, compare them
        if ((aPoints > 0) === (bPoints > 0)) {
          if (aPoints > 0) {
            // Both have points - higher points wins (sort descending)
            if (aPoints !== bPoints) {
              return bPoints - aPoints
            }
            // If points are equal, sort by time
            if (a.elapsedTime && b.elapsedTime) {
              return a.elapsedTime - b.elapsedTime
            }
          } else {
            // Both have no points - sort by time
            if (a.elapsedTime && b.elapsedTime) {
              return a.elapsedTime - b.elapsedTime
            }
          }
        } else {
          // One has points, one doesn't - the one with points wins
          return bPoints > 0 ? 1 : -1
        }
      } else {
        // No point-based scores - use original time-based sorting
        if (a.elapsedTime && b.elapsedTime) {
          return a.elapsedTime - b.elapsedTime
        }
      }

      if (a.finish_time || a.finish_time_gps) {
        return -1
      }
      if (b.finish_time || b.finish_time_gps) {
        return 1
      }
      return compareUnfinishedRunners(a, b)
    })
  }

  // Apply a live "finish" punch coming from the websocket feed, recalculate
  // standings in-place and return the affected runner (with updated position).
  function applyFinishPunch(runnerId, punchTimestamp) {
    const runner = results.value.find(r => r.id == runnerId)
    if (!runner) {
      return null
    }

    const startTime = getStartTime(runner)
    const finishTime = new Date(punchTimestamp)
    const penaltyMilliseconds = getPenaltyMilliseconds(runner.penaltySeconds)

    runner.finish_time = punchTimestamp

    if (startTime) {
      let elapsedTime = finishTime - startTime
      if (elapsedTime < 0) {
        elapsedTime = 0
      }
      elapsedTime += penaltyMilliseconds
      runner.elapsedTime = elapsedTime
      runner.timeScore = elapsedTime === 0 ? '' : getHHMMSSString(new Date(elapsedTime))
      if (runner.pointScore === null) {
        runner.score = runner.timeScore
      }
    }

    results.value = sortRunners(results.value)
    calculateLeader()
    calculateCategoryPositions()

    return results.value.find(r => r.id == runnerId)
  }

  function getOverallPosition(runner) {
    const index = results.value.findIndex(r => r.id === runner.id)
    return index === -1 ? null : index + 1
  }

  function clearResults() {
    results.value = []
    leader.value = null
  }

  function fetchEventDetails(eventAlias) {
    return fetch(`${apiUrl}/api/v1/events/alias/${eventAlias}`)
      .then(response => response.json())
      .then(data => {
        event.value = data
      })
      .catch(error => {
        console.error('Error fetching event details:', error)
      })
  }


  function getStartTime(runner) {
    if (runner.start_time) {
      return new Date(runner.start_time)
    }
    if (useStateStore().currentEvent) {
      return useStateStore().currentEvent.date_start
    }
    // Zawodnik bez własnego startu: start wydarzenia z /events/alias (wcześniej null — meta z GPS
    // dawała czas równy dacie epoki, np. 498016:20:06, a meta RFID nie miała czasu). Działa, gdy
    // /events/alias przyszło przed /splits (ResultsView czeka na nie; inne widoki — od 2. odświeżenia).
    return fallbackStart
  }

  function getHHMMSSString(date) {
    // Handle negative or zero elapsed time - don't display
    const timestamp = date instanceof Date ? date.getTime() : (typeof date === 'number' ? date : 0)
    if (timestamp <= 0) {
      return ''
    }
    const hours = Math.floor(timestamp / (1000 * 60 * 60))
    const dateObj = date instanceof Date ? date : new Date(timestamp)
    const minutes = String(dateObj.getUTCMinutes()).padStart(2, '0')
    const seconds = String(dateObj.getUTCSeconds()).padStart(2, '0')
    return `${hours}:${minutes}:${seconds}`
  }

  // finishMarker: pierwszy marker mety (8/93), runnerSplits: podbicia tego zawodnika w kolejności z API
  function getGpsFinishTime(runner, finishMarker, runnerSplits) {
    if (finishMarker) {
      const split = runnerSplits.find(split => split.marker_id == finishMarker.id)
      if (split) {
        runner.finish_time_gps = split.time
        return new Date(split.time)
      }
    }
    return null
  }

  function getMarkersMap() {
    return markers.value.reduce((map, marker) => {
      map[marker.id] = marker
      return map
    }, {})
  }

  function getSplitsMap() {
    return splits.value.reduce((map, split) => {
      if (!map[split.marker_id]) {
        map[split.marker_id] = []
      }
      map[split.marker_id].push(split)
      return map
    }, {})
  }

  function getPenaltySeconds(runner) {
    const penalty = Number(runner.penalty_seconds)
    if (!Number.isFinite(penalty) || penalty <= 0) {
      return 0
    }
    return penalty
  }

  function getPenaltyMilliseconds(penaltySeconds) {
    return penaltySeconds > 0 ? penaltySeconds * 1000 : 0
  }

  function getUnfinishedProgress(runner, orderedMarkers) {
    let count = 0
    let lastSplitTime = null
    let expectedSort = 1

    for (const marker of orderedMarkers) {
      const markerSort = Number(marker.sort)
      if (!Number.isFinite(markerSort) || markerSort < 1) {
        continue
      }
      if (markerSort < expectedSort) {
        continue
      }
      if (markerSort > expectedSort) {
        break
      }

      const split = runner.splits?.[marker.id]
      if (!split) {
        break
      }

      count += 1
      if (split.time) {
        const splitTime = new Date(split.time).getTime()
        if (Number.isFinite(splitTime)) {
          lastSplitTime = splitTime
        }
      }
      expectedSort += 1
    }

    return { count, lastSplitTime }
  }

  function compareUnfinishedRunners(a, b) {
    const aPunchedCount = a.punchedCount || 0
    const bPunchedCount = b.punchedCount || 0
    if (aPunchedCount !== bPunchedCount) {
      return bPunchedCount - aPunchedCount
    }

    const aLastSplitTime = a.unfinishedLastSplitTime ?? Number.POSITIVE_INFINITY
    const bLastSplitTime = b.unfinishedLastSplitTime ?? Number.POSITIVE_INFINITY
    if (aLastSplitTime !== bLastSplitTime) {
      return aLastSplitTime - bLastSplitTime
    }

    return a.number - b.number
  }

  // Szablony pytają o to w każdym wierszu — odpowiedź liczona raz na listę.
  const categoryColumnMemo = new WeakMap()
  const pointsColumnMemo = new WeakMap()
  const genderMemo = new WeakMap()

  function shouldShowCategoryColumn() {
    return hasGender() || memoForList(categoryColumnMemo, results.value, list => list.some(runner =>
      runner.category_id != null && !isSupportRole(runner)
    ))
  }

  function shouldShowPointsColumn() {
    return memoForList(pointsColumnMemo, results.value, list => list.some(runner =>
      runner.pointScore !== null && runner.pointScore > 0
    ))
  }

  function getCategoryName(categoryId) {
    if (!categoryId || !categories.value) return ''
    const category = categories.value.find(cat => cat.id == categoryId)
    return category ? category.name : ''
  }

  function getCategoryColor(categoryId) {
    if (!categoryId || !categories.value) return ''
    const category = categories.value.find(cat => cat.id == categoryId)
    return category ? category.color || '' : ''
  }

  // Sorts runners in place with the same rules as the main results list
  function sortRunnersForPosition(runners) {
    // Check if any runner in this category has point scores
    const hasPointScores = runners.some(r => r.pointScore !== null && r.pointScore > 0)
    
    // Sort them using the same logic as the main results
    runners.sort((a, b) => {
      if (a.abs) {
        return 1
      }
      if (b.abs) {
        return -1
      }
      if (a.dnf) {
        return 1
      }
      if (b.dnf) {
        return -1
      }
      
      // If we have point-based scores, sort by points first
      if (hasPointScores) {
        const aPoints = a.pointScore || 0
        const bPoints = b.pointScore || 0
        
        if ((aPoints > 0) === (bPoints > 0)) {
          if (aPoints > 0) {
            if (aPoints !== bPoints) {
              return bPoints - aPoints
            }
            if (a.elapsedTime && b.elapsedTime) {
              return a.elapsedTime - b.elapsedTime
            }
          } else {
            if (a.elapsedTime && b.elapsedTime) {
              return a.elapsedTime - b.elapsedTime
            }
          }
        } else {
          return bPoints > 0 ? 1 : -1
        }
      } else {
        if (a.elapsedTime && b.elapsedTime) {
          return a.elapsedTime - b.elapsedTime
        }
      }
      
      if (a.finish_time || a.finish_time_gps) {
        return -1
      }
      if (b.finish_time || b.finish_time_gps) {
        return 1
      }
      return compareUnfinishedRunners(a, b)
    })
    return runners
  }

  function getCategoryPosition(runner) {
    if (!runner.category_id || !isClassified(runner)) {
      return ''
    }

    const categoryName = getCategoryName(runner.category_id)
    if (!categoryName) return ''

    // If runner hasn't finished, show only category name
    if (!runner.finish_time && !runner.finish_time_gps) {
      return categoryName
    }

    // Get all runners in the same category
    const categoryRunners = results.value.filter(r => r.category_id == runner.category_id)
    
    sortRunnersForPosition(categoryRunners)

    // Find the position of this runner in the category
    const position = categoryRunners.findIndex(r => r.id === runner.id) + 1
    return `${categoryName} ${position}`
  }

  function hasGender() {
    return memoForList(genderMemo, results.value, list => list.some(r => r.gender === 'K' || r.gender === 'M'))
  }

  // filter: 'all' | 'K' | 'M'; unknown gender shows only under 'all'
  function filterByGender(list, filter) {
    if (!filter || filter === 'all' || !hasGender()) return list
    return list.filter(r => r.gender === filter)
  }

  // Place among runners of the same gender, e.g. "K 3" / "M 12"; '' when unknown
  function getGenderPosition(runner) {
    if ((runner.gender !== 'K' && runner.gender !== 'M') || !isClassified(runner)) {
      return ''
    }
    if (!runner.finish_time && !runner.finish_time_gps) {
      return ''
    }
    const sameGender = results.value.filter(r => r.gender === runner.gender && isClassified(r))
    sortRunnersForPosition(sameGender)
    const position = sameGender.findIndex(r => r.id === runner.id) + 1
    return position > 0 ? `${runner.gender} ${position}` : ''
  }

  // Pre-calculate category and K/M positions for all runners. Same result as calling
  // getCategoryPosition / getGenderPosition per runner (same groups in the same input order,
  // same comparator), but every group is sorted once instead of once per runner.
  function calculateCategoryPositions(list = results.value) {
    const byCategory = new Map()
    const byGender = new Map()
    list.forEach(runner => {
      if (runner.category_id != null && runner.category_id !== '') {
        const key = String(runner.category_id)
        if (!byCategory.has(key)) byCategory.set(key, [])
        byCategory.get(key).push(runner)
      }
      if ((runner.gender === 'K' || runner.gender === 'M') && isClassified(runner)) {
        if (!byGender.has(runner.gender)) byGender.set(runner.gender, [])
        byGender.get(runner.gender).push(runner)
      }
    })
    const placeIn = groups => {
      const place = new Map()
      groups.forEach(group => {
        sortRunnersForPosition(group).forEach((r, index) => {
          if (!place.has(r.id)) place.set(r.id, index + 1)
        })
      })
      return place
    }
    const categoryPlace = placeIn(byCategory)
    const genderPlace = placeIn(byGender)

    list.forEach(runner => {
      runner.categoryPosition = categoryPositionText(runner, categoryPlace)
      runner.genderPosition = genderPositionText(runner, genderPlace)
    })
  }

  function categoryPositionText(runner, categoryPlace) {
    if (!runner.category_id || !isClassified(runner)) return ''
    const categoryName = getCategoryName(runner.category_id)
    if (!categoryName) return ''
    if (!runner.finish_time && !runner.finish_time_gps) return categoryName
    return `${categoryName} ${categoryPlace.get(runner.id) || 0}`
  }

  function genderPositionText(runner, genderPlace) {
    if ((runner.gender !== 'K' && runner.gender !== 'M') || !isClassified(runner)) return ''
    if (!runner.finish_time && !runner.finish_time_gps) return ''
    const position = genderPlace.get(runner.id) || 0
    return position > 0 ? `${runner.gender} ${position}` : ''
  }

  function calculateLeader(list = results.value) {
    // Check if we have point-based scoring
    const hasPointScores = list.some(r => r.pointScore !== null && r.pointScore > 0)

    if (hasPointScores) {
      // For point-based scoring, leader is the one with highest points (and fastest time as tiebreaker)
      leader.value = list.find(r =>
        !r.abs && !r.dnf && isClassified(r) && r.pointScore > 0
      ) || null
    } else {
      // For time-based scoring, leader is the fastest finisher
      leader.value = list.find(r =>
        !r.abs && !r.dnf && r.elapsedTime != null && isClassified(r)
      ) || null
    }
  }

  function getGapToLeader(runner, hasPointsOverride) {
    // If no leader found or this runner hasn't finished
    if (!leader.value || runner.abs || runner.dnf) {
      return null
    }

    // If this is the leader
    if (runner.id === leader.value.id) {
      return { timeGap: 0, pointGap: 0 }
    }

    // hasPointsOverride lets callers that already computed this once (e.g. per-list)
    // avoid re-scanning all results for every single row
    const hasPoints = hasPointsOverride !== undefined ? hasPointsOverride : shouldShowPointsColumn()
    
    let timeGap = null
    let pointGap = null
    
    // Calculate time gap
    if (runner.elapsedTime && leader.value.elapsedTime) {
      timeGap = runner.elapsedTime - leader.value.elapsedTime
    }
    
    // Calculate point gap if using point-based scoring
    if (hasPoints) {
      const runnerPoints = runner.pointScore || 0
      const leaderPoints = leader.value.pointScore || 0
      pointGap = leaderPoints - runnerPoints // Leader has more points, so positive gap means runner is behind
    }
    
    return { timeGap, pointGap }
  }


  return { 
    results, 
    markersMap, 
    splitsMap, 
    event, 
    categories, 
    fetchResults, 
    fetchEventDetails,
    clearResults, 
    shouldShowCategoryColumn,
    shouldShowPointsColumn,
    getCategoryPosition,
    getGenderPosition,
    hasGender,
    filterByGender,
    getCategoryColor,
    getGapToLeader,
    applyFinishPunch,
    getOverallPosition
  }
})